import { useLazyQuery, useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Input } from "components/base/Form";
import SearchBar from "components/base/SearchBar";
import { Alert } from "components/base/Toast";
import CircleContainerizationView from "components/shared/CircleContainerizationView";
import PageV2 from "components/shared/PageV2";
import ProductSelector from "components/shared/ProductSelector";
import { useModals } from "ModalProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import XLSX from "xlsx";
import ForecastProductView from "../ForecastProductView";
import { FETCH_PETE_FCST } from "../graphql";
import {
  FETCH_FORECAST_CATEGORY_STOCK_HISTORY,
  FORECAST_CATEGORY_SAVE_SUBMISSION,
  SAVE_FORECAST_CATEGORY,
} from "./graphql";

function parseStockHistoryList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function normalizeMonth(rawMonth) {
  if (!rawMonth) return null;
  const value = String(rawMonth).trim();
  if (/^\d{4}-\d{2}$/.test(value)) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value.slice(0, 7);
  return null;
}

function getNextMonth() {
  const nextMonthDate = new Date();
  nextMonthDate.setDate(1);
  nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
  const year = nextMonthDate.getFullYear();
  const month = String(nextMonthDate.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeProductId(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) return String(parseInt(raw, 10));
  return raw;
}

function normalizeStockHistoryByProductId(stockHistory) {
  const byProductId = {};

  stockHistory.forEach((i) => {
    const productId = normalizeProductId(i.wis_product_id);
    if (!productId) return;

    const month = normalizeMonth(i.year_month);
    if (!month) return;

    const qty = toNumber(i.qty);

    if (!byProductId[productId]) byProductId[productId] = {};
    if (!byProductId[productId][month]) byProductId[productId][month] = 0;
    byProductId[productId][month] += qty;
  });

  return byProductId;
}

function parseSubmissionData(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

const CategorySubmitForecastPage = ({ category }) => {
  const stockHistory = parseStockHistoryList(category.stockHistory);

  const stockHistoryByProductId =
    normalizeStockHistoryByProductId(stockHistory);
  const tableScrollRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const isFirstRender = useRef(true);
  const didInitialScrollRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams({
    searchText: "",
    hasFcstedOnly: "false",
    showStockHistory: "true",
  });
  const params = Object.fromEntries(searchParams.entries());
  const searchText = params.searchText || "";
  const hasFcstedOnly = params.hasFcstedOnly === "true";
  const showStockHistory = params.showStockHistory !== "false";
  const initialSubmissionData = parseSubmissionData(
    category?.lastSubmission?.data,
  );
  const initialColumns = (() => {
    const savedColumns = (initialSubmissionData.columns || [])
      .map((month, index) => {
        const normalized = normalizeMonth(month);
        if (!normalized) return null;
        return { id: `saved-${normalized}-${index}`, month: normalized };
      })
      .filter(Boolean);

    if (savedColumns.length > 0) return savedColumns;

    const nextMonth = getNextMonth();
    return [{ id: `default-${nextMonth}`, month: nextMonth }];
  })();
  const [forecastColumns, setForecastColumns] = useState(initialColumns);
  const [fcstData, setFcstData] = useState(() => {
    const next = {};
    const items = Array.isArray(initialSubmissionData.items)
      ? initialSubmissionData.items
      : [];
    items.forEach((item) => {
      const productId = normalizeProductId(item?.wis_product_id);
      if (!productId) return;
      const monthMap = item?.fcst || {};
      if (!monthMap || typeof monthMap !== "object") return;
      const monthData = {};
      Object.entries(monthMap).forEach(([month, qty]) => {
        const normalized = normalizeMonth(month);
        if (!normalized) return;
        monthData[normalized] = toNumber(qty);
      });
      next[productId] = monthData;
    });
    return next;
  });
  const [lastSavedAt, setLastSavedAt] = useState(
    category?.lastSubmission?.updatedAt
      ? new Date(category.lastSubmission.updatedAt)
      : null,
  );

  const [fetchStockHistory, fetchStockHistoryRes] = useLazyQuery(
    FETCH_FORECAST_CATEGORY_STOCK_HISTORY,
  );
  const [fetchPeteFcst, fetchPeteFcstRes] = useLazyQuery(FETCH_PETE_FCST, {
    fetchPolicy: "network-only",
    onCompleted: handlePeteFcstCompleted,
    onError: (error) => {
      window.alert(parseError(error));
    },
  });

  function handlePeteFcstCompleted(data) {
    let peteFcst = {};
    try {
      const parsed = JSON.parse(data?.salesPeteForecast || "{}");
      peteFcst = parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      window.alert("Failed to parse Pete forecast data.");
      return;
    }

    const numberToId = {};
    products.forEach((product) => {
      numberToId[product.number] = normalizeProductId(product.id);
    });

    const unmapped = Object.keys(peteFcst).filter(
      (number) => !numberToId[number],
    );
    const mapped = Object.keys(peteFcst).filter((number) => numberToId[number]);

    const importedMonthSet = new Set();
    Object.entries(peteFcst).forEach(([number, monthData]) => {
      if (!numberToId[number]) return;
      if (!monthData || typeof monthData !== "object") return;
      Object.keys(monthData).forEach((rawMonth) => {
        const month = normalizeMonth(rawMonth);
        if (month) importedMonthSet.add(month);
      });
    });
    const importedMonths = [...importedMonthSet].sort((a, b) =>
      a.localeCompare(b),
    );

    setForecastColumns(
      importedMonths.map((month, index) => ({
        id: `pete-${month}-${index}`,
        month,
      })),
    );

    setFcstData(() => {
      const next = {};
      products.forEach((product) => {
        const productId = normalizeProductId(product.id);
        next[productId] = importedMonths.reduce((acc, month) => {
          acc[month] = 0;
          return acc;
        }, {});
      });

      Object.entries(peteFcst).forEach(([number, monthData]) => {
        const productId = numberToId[number];
        if (!productId || !monthData || typeof monthData !== "object") return;
        Object.entries(monthData).forEach(([rawMonth, qty]) => {
          const month = normalizeMonth(rawMonth);
          if (!month || !importedMonthSet.has(month)) return;
          next[productId][month] = toNumber(qty);
        });
      });

      return next;
    });

    window.alert(
      `${mapped.length} products imported successfully.` +
        (unmapped.length > 0
          ? `\n\nCould not find ${unmapped.length} SKU(s) in our system: ${unmapped.join(", ")}.\n\nPlease make sure the SKU matches the item number. Contact us if you need help.`
          : ""),
    );
  }

  function importIncFcstSheetHandler() {
    if (
      window.confirm(
        `Import forecast data from Pete's Fcst sheet? WIS can read this data and update the forecast accordingly. For FSI, wis will ready the "Forecast by Month" tab, and for Amazon, wis will read the "AMZ FOB Forecast by Month" tab. This will clear and overwrite current values in this page.`,
      )
    ) {
      fetchPeteFcst({
        variables: { sheetName: category.incFcstSheetName },
      });
    }
  }

  const [saveCategory, saveCategoryRes] = useMutation(SAVE_FORECAST_CATEGORY, {
    onError(error) {
      window.alert(parseError(error));
    },
    refetchQueries: ["FETCH_FORECAST_CATEGORY_AND_SALES_INFO"],
    awaitRefetchQueries: true,
  });

  const [saveSubmission] = useMutation(FORECAST_CATEGORY_SAVE_SUBMISSION, {
    onCompleted(res) {
      const updatedAt =
        res?.forecastCategorySaveSubmission?.submission?.updatedAt || null;
      setLastSavedAt(updatedAt ? new Date(updatedAt) : new Date());
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  const modal = useModals();
  const stockHistoryMonthSet = new Set();
  Object.values(stockHistoryByProductId || {}).forEach(
    (productStockHistory) => {
      if (!productStockHistory || typeof productStockHistory !== "object")
        return;
      Object.keys(productStockHistory).forEach((month) => {
        const normalized = normalizeMonth(month);
        if (normalized) stockHistoryMonthSet.add(normalized);
      });
    },
  );

  const stockHistoryMonths = [...stockHistoryMonthSet].sort((a, b) =>
    a.localeCompare(b),
  );
  const visibleStockHistoryMonths = showStockHistory ? stockHistoryMonths : [];

  const products = [...(category.products || [])].sort((a, b) =>
    (a.number || "").localeCompare(b.number || "", undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );

  const keyword = searchText.trim().toLowerCase();
  const searchedProducts = !keyword
    ? products
    : products.filter((product) => {
        const number = String(product.number || "").toLowerCase();
        const name = String(product.name || "").toLowerCase();
        return number.includes(keyword) || name.includes(keyword);
      });

  const getStockHistoryQty = (productId, month) => {
    const monthlyStockHistory =
      stockHistoryByProductId?.[normalizeProductId(productId)];
    if (!monthlyStockHistory || typeof monthlyStockHistory !== "object")
      return 0;
    return toNumber(monthlyStockHistory[month] ?? 0);
  };

  const getForecastQty = (productId, month) =>
    toNumber(fcstData?.[normalizeProductId(productId)]?.[month] ?? 0);

  const hasFcstColumns = forecastColumns.some((column) =>
    Boolean(normalizeMonth(column.month)),
  );
  const hasFcstData = hasFcstColumns
    ? products.some((product) =>
        forecastColumns.some((column) => {
          const month = normalizeMonth(column.month);
          if (!month) return false;
          return getForecastQty(product.id, month) > 0;
        }),
      )
    : false;
  const shouldShowHasFcstedOnlyFilter = hasFcstColumns && hasFcstData;

  const filteredProducts =
    shouldShowHasFcstedOnlyFilter && hasFcstedOnly
      ? searchedProducts.filter((product) =>
          forecastColumns.some((column) => {
            const month = normalizeMonth(column.month);
            if (!month) return false;
            return getForecastQty(product.id, month) > 0;
          }),
        )
      : searchedProducts;
  const monthCbmTotals = useMemo(() => {
    const totals = {};
    forecastColumns.forEach((column) => {
      const month = normalizeMonth(column.month);
      if (!month) return;
      totals[month] = products.reduce((sum, product) => {
        const productId = normalizeProductId(product.id);
        const qty = toNumber(fcstData?.[productId]?.[month] ?? 0);
        const itemsPerSet = Number(product.itemsPerSet) || 0;
        const cbmPerCarton = Number(product.cbm) || 0;
        if (itemsPerSet <= 0 || cbmPerCarton <= 0 || qty <= 0) return sum;
        const cartonCount = qty / itemsPerSet;
        return sum + cartonCount * cbmPerCarton;
      }, 0);
    });
    return totals;
  }, [forecastColumns, products, fcstData]);

  const totalCbm = useMemo(() => {
    const months = [
      ...new Set(
        forecastColumns
          .map((column) => normalizeMonth(column.month))
          .filter(Boolean),
      ),
    ];
    return months.reduce((sum, month) => sum + (monthCbmTotals[month] || 0), 0);
  }, [forecastColumns, monthCbmTotals]);

  const shippingZone = useMemo(
    () => (category.mode === "INC" ? "US" : "EU"),
    [category.mode],
  );

  const formatCbm = (value) =>
    value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });

  const setForecastQty = (productId, month, value) => {
    if (!month) return;
    const parsed = value === "" ? 0 : parseInt(value, 10);
    if (Number.isNaN(parsed)) return;
    const normalizedProductId = normalizeProductId(productId);
    setFcstData((prev) => ({
      ...prev,
      [normalizedProductId]: {
        ...(prev[normalizedProductId] || {}),
        [month]: parsed,
      },
    }));
  };

  function addProduct() {
    modal.present({
      title: "Add Product",
      children: (
        <ProductSelector
          onSelect={(product) => {
            if (products.some((p) => p.id === product.id)) {
              window.alert(
                `Product ${product.number} already in this category.`,
              );
              return;
            }

            saveCategory({
              variables: {
                id: category.id,
                productIds: [...products.map((p) => p.id), product.id],
              },
            });

            modal.hide();
          }}
        />
      ),
    });
  }

  async function importHandler() {
    if (
      window.confirm(
        "Are you sure to import products from order history? This will only add new products that are not already in this category.",
      ) === false
    )
      return;

    const res = await fetchStockHistory({
      variables: { id: category.id },
      fetchPolicy: "network-only",
    });
    const rawStockHistory = res?.data?.forecastCategory?.stockHistory;

    let stockHistory = [];
    try {
      const parsed = JSON.parse(rawStockHistory || "[]");
      stockHistory = Array.isArray(parsed) ? parsed : [];
    } catch {
      window.alert("Failed to parse stock history data.");
      return;
    }

    const stockHistoryProductIds = [
      ...new Set(
        stockHistory
          .map((item) => String(item?.wis_product_id || "").trim())
          .filter(Boolean),
      ),
    ];

    if (stockHistoryProductIds.length === 0) {
      window.alert("No products found from stock history.");
      return;
    }

    const existingProductIds = new Set(
      (category.products || []).map((product) => String(product.id).trim()),
    );
    const newProductIds = stockHistoryProductIds.filter(
      (productId) => !existingProductIds.has(productId),
    );

    if (newProductIds.length === 0) {
      window.alert(
        "All products from stock history are already in this category.",
      );
      return;
    }

    if (
      !window.confirm(
        `Found ${newProductIds.length} new products from stock history. Do you want to add them to this category?`,
      )
    )
      return;

    await saveCategory({
      variables: {
        id: category.id,
        productIds: [...existingProductIds, ...newProductIds],
      },
    });
  }

  function removeProductHandler(productId) {
    if (window.confirm("Are you sure to remove this product?") === false)
      return;

    saveCategory({
      variables: {
        id: category.id,
        productIds: products.filter((p) => p.id !== productId).map((p) => p.id),
      },
    });
  }

  const addCustomColumn = () => {
    setForecastColumns((prev) => [
      ...prev,
      {
        id: `custom-${Date.now()}-${prev.length}`,
        month: "",
      },
    ]);

    requestAnimationFrame(() => {
      if (!tableScrollRef.current) return;
      tableScrollRef.current.scrollLeft = tableScrollRef.current.scrollWidth;
    });
  };

  const removeCustomColumn = (columnId) => {
    if (
      window.confirm("Are you sure to remove this forecast column?") === false
    )
      return;

    setForecastColumns((prev) =>
      prev.filter((column) => column.id !== columnId),
    );
    const removedColumn = forecastColumns.find(
      (column) => column.id === columnId,
    );
    if (!removedColumn?.month) return;
    setFcstData((prev) => {
      const next = {};
      Object.entries(prev).forEach(([productId, productData]) => {
        const restData = { ...(productData || {}) };
        delete restData[removedColumn.month];
        next[productId] = restData;
      });
      return next;
    });
  };

  const changeCustomColumnMonth = (columnId, monthValue) => {
    const normalized = normalizeMonth(monthValue) || "";
    const hasDuplicate = forecastColumns.some(
      (column) => column.id !== columnId && column.month === normalized,
    );
    if (normalized && hasDuplicate) {
      window.alert("This month already exists.");
      return;
    }
    const oldMonth = forecastColumns.find(
      (column) => column.id === columnId,
    )?.month;
    setForecastColumns((prev) =>
      prev.map((column) =>
        column.id === columnId ? { ...column, month: normalized } : column,
      ),
    );
    if (!oldMonth || oldMonth === normalized) return;
    setFcstData((prev) => {
      const next = {};
      Object.entries(prev).forEach(([productId, productData]) => {
        const item = { ...(productData || {}) };
        if (Object.prototype.hasOwnProperty.call(item, oldMonth)) {
          item[normalized] = toNumber(item[oldMonth]);
          delete item[oldMonth];
        }
        next[productId] = item;
      });
      return next;
    });
  };

  function submitHandler() {
    if (
      window.confirm(
        "Are you sure to submit the forecast? Submitting a forecast means that you have finalized the forecast for this category now and it will be shared to the relevant parties. You will not be able to edit the forecast after submission. You can always submit a new forecast later if needed.",
      ) === false
    )
      return;

    saveSubmission({
      variables: {
        forecastCategoryId: category.id,
        submittedAt: new Date().toISOString(),
      },
    }).then(() => {
      Alert("success", "Forecast submitted successfully.");
    });
  }

  const buildSubmissionPayload = () => {
    const columns = forecastColumns
      .map((column) => normalizeMonth(column.month))
      .filter(Boolean);
    const items = products.map((product) => {
      const productId = normalizeProductId(product.id);
      const monthData = fcstData?.[productId] || {};
      const fcst = {};
      columns.forEach((month) => {
        fcst[month] = toNumber(monthData[month] ?? 0);
      });
      return { wis_product_id: productId, fcst };
    });
    return { columns, items };
  };

  const doAutoSave = useCallback(
    (payload) => {
      saveSubmission({
        variables: {
          forecastCategoryId: category.id,
          data: JSON.stringify(payload),
        },
      });
    },
    [saveSubmission, category.id],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      doAutoSave(buildSubmissionPayload());
    }, 500);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [forecastColumns, fcstData, doAutoSave]);

  useEffect(() => {
    if (didInitialScrollRef.current) return;
    didInitialScrollRef.current = true;
    requestAnimationFrame(() => {
      if (!tableScrollRef.current) return;
      tableScrollRef.current.scrollLeft = tableScrollRef.current.scrollWidth;
    });
  }, []);

  function exportHandler() {
    if (products.length === 0) {
      window.alert("No products to export.");
      return;
    }

    if (window.confirm("Export stock history data to Excel?") === false) return;

    const data = products.map((product) => {
      const row = {
        "WIS Product ID": normalizeProductId(product.id),
        "Number / SKU": product.number || "",
        "Product Name": product.name || "",
        "QTY / CTN": toNumber(product.itemsPerSet),
      };

      stockHistoryMonths.forEach((month) => {
        row[`${month}`] = getStockHistoryQty(product.id, month);
      });

      return row;
    });

    const safeCategoryName = String(category?.name || "category")
      .replace(/[\\/:*?"<>|]/g, "_")
      .trim();
    const sheet = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "forecast");
    const filename = `Forecast_${safeCategoryName || "category"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
  }

  return (
    <PageV2
      title={`${category.mode} / Submit Forecast: ${category?.name || "No Name?"}`}
      backTo="./.."
      toolbar={
        <div className="flex items-center space-x-6">
          {lastSavedAt ? (
            <span className="text-xs text-gray-500 whitespace-nowrap">
              ✔ Auto saved at {lastSavedAt.toLocaleString()}
            </span>
          ) : null}

          {category.incFcstSheetName && (
            <Button
              onClick={importIncFcstSheetHandler}
              loading={fetchPeteFcstRes.loading}
              disabled={saveCategoryRes.loading}
            >
              Import from Fcst Sheet (Pete)
            </Button>
          )}

          {category.connectionType === "CUSTOMER" && category.customer && (
            <Button
              onClick={importHandler}
              loading={fetchStockHistoryRes.loading}
              disabled={saveCategoryRes.loading}
            >
              Import Products from Orders
            </Button>
          )}

          {category.connectionType === "STOCK_CATEGORY" &&
            category.stockCategory && (
              <Button
                onClick={importHandler}
                loading={fetchStockHistoryRes.loading}
                disabled={saveCategoryRes.loading}
              >
                Import Products from Stock History
              </Button>
            )}
        </div>
      }
    >
      <div className="h-full flex flex-col space-y-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <SearchBar
              value={searchText}
              onChange={(nextSearchText) =>
                setSearchParams({ ...params, searchText: nextSearchText })
              }
              placeholder="Search by SKU or product name"
            />
          </div>

          {stockHistoryMonths.length > 0 && (
            <>
              <div className="flex whitespace-nowrap">
                <input
                  id="toggle-stock-history"
                  type="checkbox"
                  className="mr-1 align-middle"
                  checked={showStockHistory}
                  onChange={(e) =>
                    setSearchParams({
                      ...params,
                      showStockHistory: e.target.checked ? "true" : "false",
                    })
                  }
                />
                <label htmlFor="toggle-stock-history">Show Stock History</label>
              </div>
              <Button onClick={exportHandler}>Export History Data Excel</Button>
            </>
          )}

          <Button onClick={addProduct} loading={saveCategoryRes.loading}>
            + Product
          </Button>
          <Button disabled={products.length === 0} onClick={addCustomColumn}>
            + Fcst Col
          </Button>
          {shouldShowHasFcstedOnlyFilter && (
            <div className="flex items-center whitespace-nowrap">
              <input
                id="has-fcsted-only"
                type="checkbox"
                className="mr-1"
                checked={hasFcstedOnly}
                onChange={(e) =>
                  setSearchParams({
                    ...params,
                    hasFcstedOnly: e.target.checked ? "true" : "false",
                  })
                }
              />
              <label htmlFor="has-fcsted-only">Has Fcsted Only</label>
            </div>
          )}
          <Button primary onClick={submitHandler}>
            Submit Forecast
          </Button>
        </div>

        <div
          ref={tableScrollRef}
          className="card p-0 flex-1 overflow-auto text-xs"
        >
          <table className="w-full ">
            <thead>
              <tr className="sticky top-0 backdrop-blur bg-gray-200 bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-80 border-b border-gray-200 dark:border-gray-700 z-20 whitespace-nowrap">
                <th className="sticky left-0 z-30 px-4 py-3 text-left border-r border-gray-200 dark:border-gray-700 bg-gray-200 bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-80">
                  Product ({filteredProducts.length})
                </th>
                {visibleStockHistoryMonths.map((month) => (
                  <th
                    key={`stock-history-${month}`}
                    className="px-2 py-3 text-right border-x border-gray-200 dark:border-gray-700"
                  >
                    {month}
                  </th>
                ))}
                {forecastColumns.map((column) => (
                  <th
                    key={column.id}
                    className="px-2 py-3 text-right whitespace-nowrap"
                  >
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        trash
                        onClick={() => removeCustomColumn(column.id)}
                      />
                      <Input
                        type="month"
                        className="text-right px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                        value={column.month}
                        onChange={(e) =>
                          changeCustomColumnMonth(column.id, e.target.value)
                        }
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr
                  key={product.id}
                  className="border-y border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td className="sticky left-0 z-10 px-4 py-2 border-r border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center space-x-2">
                      <Button
                        trash
                        onClick={() => removeProductHandler(product.id)}
                        disabled={saveCategoryRes.loading}
                      />
                      <div>
                        <ForecastProductView product={product} />
                      </div>
                    </div>
                  </td>
                  {visibleStockHistoryMonths.map((month) => (
                    <td
                      key={`${product.id}-stock-history-${month}`}
                      className="px-2 py-2 text-right border-x border-gray-100 dark:border-gray-700"
                    >
                      {getStockHistoryQty(product.id, month).toLocaleString()}
                    </td>
                  ))}
                  {forecastColumns.map((column) => (
                    <td key={`${product.id}-${column.id}`} className="py-1">
                      <div className="flex justify-end">
                        <Input
                          className="text-right bg-blue-50"
                          value={
                            column.month
                              ? getForecastQty(product.id, column.month)
                              : 0
                          }
                          disabled={!column.month}
                          onChange={(e) =>
                            setForecastQty(
                              product.id,
                              column.month,
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
              {!filteredProducts.length ? (
                <tr>
                  <td
                    colSpan={
                      1 +
                      visibleStockHistoryMonths.length +
                      forecastColumns.length
                    }
                    className="px-4 py-6 opacity-70"
                  >
                    {products.length > 0
                      ? "No products found."
                      : "No products in this category. Please add products to this category first."}
                  </td>
                </tr>
              ) : null}
            </tbody>
            {totalCbm > 0 && (
              <tfoot>
                <tr className="sticky bottom-0 backdrop-blur bg-gray-200 bg-opacity-90 dark:bg-gray-800 dark:bg-opacity-90 border-t border-gray-300 dark:border-gray-600 z-20">
                  <th className="sticky left-0 z-30 px-4 py-3 text-left border-r border-gray-300 dark:border-gray-600 bg-gray-200 bg-opacity-90 dark:bg-gray-800 dark:bg-opacity-90">
                    <div>Total CBM ({formatCbm(totalCbm)}m³)</div>
                    <p className="font-normal opacity-70">
                      The total CBM algorithm is trying to find the lowest cost
                      first, then the smallest total container capacity (the
                      highest possible loading rate).
                    </p>
                  </th>
                  {visibleStockHistoryMonths.map((month) => (
                    <th
                      key={`stock-history-cbm-${month}`}
                      className="px-2 py-3 text-right whitespace-nowrap border-x border-gray-300 dark:border-gray-600"
                    >
                      -
                    </th>
                  ))}
                  {forecastColumns.map((column) => {
                    const month = normalizeMonth(column.month);
                    return (
                      <th
                        key={`forecast-cbm-${column.id}`}
                        className="px-2 py-3 text-right whitespace-nowrap"
                      >
                        {month ? (
                          <CircleContainerizationView
                            totalCbm={monthCbmTotals[month] || 0}
                            shippingZone={shippingZone}
                            defaultDisabledContainers={["40GP"]}
                            displayTotalCbm={formatCbm(
                              monthCbmTotals[month] || 0,
                            )}
                            className="text-right"
                          />
                        ) : (
                          "-"
                        )}
                      </th>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="opacity-70 flex justify-between space-x-6">
          <div className="space-y-1">
            <p>
              Input the forecast quantities for each product and month(start
              shipping). Once you are done, click the "Submit" button on the top
              right to submit your forecast. We will use this data to plan our
              production and inventory for the upcoming months.
            </p>
            {category.incFcstSheetName && (
              <p>
                <a
                  href="https://wabobaadmin-my.sharepoint.com/:x:/r/personal/pete_waboba_com/_layouts/15/Doc.aspx?sourcedoc=%7B6C85D757-58D4-4EA0-9B44-8DE4344FE33F%7D&file=2026%20Inv%20Forecast.xlsx&fromShare=true&action=default&mobileredirect=true"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click here to open Pete's forecast sheet
                </a>
              </p>
            )}
          </div>

          <div className="flex space-x-4 whitespace-nowrap">
            {products.length > 0 && (
              <Button
                danger
                onClick={() => {
                  if (
                    window.confirm(
                      "Are you sure to remove all products from this category?",
                    ) === false
                  )
                    return;
                  saveCategory({
                    variables: { id: category.id, productIds: [] },
                  });
                }}
                disabled={saveCategoryRes.loading}
              >
                Remove All Products
              </Button>
            )}
          </div>
        </div>
      </div>
    </PageV2>
  );
};

export default CategorySubmitForecastPage;
